import React, { useState, useEffect, useContext, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { useSelector, useDispatch } from '../../../redux/store';
import AdminContext from '../../../Context/adminContext';
import SearchBar from '../../../Components/SearchBar/SearchBar';
import styles from './ClientList.module.css';
import {
  getClientsList,
  setTypeFilter,
  setTagsFilter,
  setProfessionFilter,
  setPreferredFilter,
  setSearchTerm,
  setinputValue,
  setCurrentPage,
  setSortColumn,
  setSortDirection,
  fetchClientExport,
  attachClientRelation,
} from "../../../redux/slices/client";
import {
  getRunners,
  createClient
} from "../../../redux/slices/opportunitiesdata";
import ClientForm from '../../../Components/ClientForm/ClientForm';
import Dropdown from "../../../Components/DropDownBox/Dropdown";
import ApiResponseDisplay from '../../../Components/ApiResponseDisplay/ApiResponseDisplay';
import { hasError } from '../../../redux/slices/client';
import ClientListTable from '../../../Sections/clients/ClientListTable';
import ProjectTablePagination from '../../Projects/ProjectList/ProjectTablePagination';
import { Box, CircularProgress, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportButtonSx } from '../../../GlobalStyles/buttonStyles';

function ClientList() {
  const { accounts } = useMsal();
  const dispatch = useDispatch();
  const account = accounts[0];
  const admin = useContext(AdminContext);
  const [addNewPopUp, setAddNewPopUp] = useState(false);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [typeOptions, setTypeOptions] = useState(['All', 'Company', 'Home Owner', 'Professional']);
  // Remove tag filtering entirely for clients
  const [tagOptions, setTagOptions] = useState(['All']);
  const [professionOptions, setProfessionOptions] = useState(['All', 'Architect', 'Contractor', 'Developer']);
  const [preferredOptions, setPreferredOptions] = useState(['All', 'Work with - Preferred', 'Work with - Not Preferred', "Do not work with"]);
  const [poiOptions] = useState(['All', 'Person of Interest']);
  const [clientExportLoading, setClientExportLoading] = useState(false);

  const {
    currentPage,
    clientList,
    tagsFilter,
    typeFilter,
    professionFilter,
    preferredFilter,
    searchTerm,
    inputValue,
    clientListLoading,
    sortDirection,
    sortColumn,
    error,
  } = useSelector((state) => state.client);


  const totalPages = clientList.totalRows > 0 ? Math.ceil(clientList.totalRows / rowsPerPage) : 0;
  const currentPageValue = clientList.totalRows > 0 ? currentPage : 0;

  // Prevent duplicate initial fetches when dependencies have not changed
  const lastQueryRef = useRef(null);

  useEffect(() => {
    const queryKey = JSON.stringify({
      typeFilter,
      tagsFilter,
      professionFilter,
      preferredFilter,
      searchTerm,
      currentPage,
      rowsPerPage,
      sortColumn,
      sortDirection,
    });

    if (lastQueryRef.current === queryKey) {
      // Skip re-dispatch if nothing has actually changed
      return;
    }
    lastQueryRef.current = queryKey;

    dispatch(getClientsList(typeFilter, tagsFilter, professionFilter, preferredFilter, searchTerm, currentPage, rowsPerPage, sortColumn, sortDirection));
    setPaginationLoading(false);
    dispatch(getRunners());
  }, [typeFilter, tagsFilter, professionFilter, preferredFilter, sortColumn, sortDirection, currentPage, searchTerm, rowsPerPage, dispatch]);

  const handleSort = (column) => {
    if (column === sortColumn) {
      dispatch(setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'));
    } else {
      dispatch(setSortColumn(column));
      dispatch(setSortDirection('asc'));
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setPaginationLoading(true);
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPaginationLoading(true);
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
      setPaginationLoading(true);
      dispatch(setCurrentPage(pageNumber));
    }
  };

  const handleSearch = (event) => {
    dispatch(setinputValue(event.target.value));
  };

  const handleSearchSubmit = () => {
    dispatch(setSearchTerm(inputValue));
    dispatch(setCurrentPage(1));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleAddNewClientSubmit = async (formData) => {
    try {
      // Strip internal linking fields before calling the create API
      // so the backend doesn't see unknown properties.
      const { linkedCompanyID, linkedCompanyName, ...apiPayload } = formData || {};

      const createdClient = await dispatch(createClient(apiPayload));

      if (
        createdClient &&
        formData.clientTypes === 'Company' &&
        formData.linkedCompanyID
      ) {
        await dispatch(
          attachClientRelation({
            clientID: createdClient.id, // base: new company
            relatedClientID: formData.linkedCompanyID,
            relationType: 'professional',
            updatedBy: account.username,
          })
        );
      }
    } catch (error) {
      console.error("Error creating client or attaching company:", error);
    } finally {
      setAddNewPopUp(false);
      dispatch(setinputValue(''));
      dispatch(setSearchTerm(''));
    }
  }

  const handleTypeFilter = (type) => {
    dispatch(setTypeFilter(type));
    dispatch(setCurrentPage(1));

    // Reset profession filter if the new type doesn't use it
    if (!['All', 'Professional', 'Company'].includes(type)) {
      dispatch(setProfessionFilter("All"));
    }
  };

  const handleTagsFilter = (type) => {
    dispatch(setTagsFilter(type));
    dispatch(setCurrentPage(1));
  };

  const handleProfessionFilter = (profession) => {
    dispatch(setProfessionFilter(profession));
    dispatch(setCurrentPage(1));
  };

  const handlePreferredFilter = (preferred) => {
    dispatch(setPreferredFilter(preferred));
    dispatch(setCurrentPage(1));
  };

  const handleClientExportClick = async () => {
    setClientExportLoading(true);
    try {
      await dispatch(fetchClientExport());
    } catch (error) {
      console.error("Client export failed:", error);
    } finally {
      setClientExportLoading(false);
    }
  };

  return (
    <div className={styles.ClientMainContent}>
      <div className={styles.ClientHeader}>
        <div className={styles.ClientTitle}>
          Client List
        </div>
        {admin &&
          <Button
            variant="contained"
            onClick={handleClientExportClick}
            disabled={clientListLoading || clientExportLoading}
            startIcon={<FileDownloadIcon sx={{ fontSize: '16px' }}/>}
            sx={{ ...exportButtonSx, marginLeft: '10px' }}
          >
            {clientExportLoading ? 'Exporting...' : 'Export'}
          </Button>
        }
      </div>

      <div className={styles.ClientTableFilters}>
        <div className={styles.Filters}>
          <div className={styles.FilterContainer}>
            <label className={styles.FilterLabel} htmlFor='TypeFilter' style={{ fontSize: '12px' }}>Type:</label>
            <div className={styles.dropdownContainer}>
              <Dropdown options={typeOptions} initialSelectedValue={typeFilter} onValueChange={handleTypeFilter} disabled={clientListLoading} />
            </div>
          </div>

          {['All', 'Professional', 'Company'].includes(typeFilter) && (
            <div className={styles.FilterContainer}>
              <label className={styles.FilterLabel} htmlFor='ProfessionFilter' style={{ fontSize: '12px' }}>Profession:</label>
              <div className={styles.dropdownContainer}>
                <Dropdown options={professionOptions} initialSelectedValue={professionFilter} onValueChange={handleProfessionFilter} disabled={clientListLoading} />
              </div>
            </div>
          )}

          {/* Person of Interest quick filter */}
          {['All', 'Professional', 'Company'].includes(typeFilter) && (
            <div className={styles.FilterContainer}>
              <label className={styles.FilterLabel} htmlFor='POIFilter' style={{ fontSize: '12px' }}>POI:</label>
              <div className={styles.dropdownContainer}>
                <Dropdown options={poiOptions} initialSelectedValue={tagsFilter === 'Person of Interest' ? 'Person of Interest' : 'All'} onValueChange={(v)=>dispatch(setTagsFilter(v === 'Person of Interest' ? 'Person of Interest' : 'All'))} disabled={clientListLoading} />
              </div>
            </div>
          )}

          {['All', 'Professional', 'Company', 'Home Owner'].includes(typeFilter) && (
            <div className={styles.FilterContainer}>
              <label className={styles.FilterLabel} htmlFor='PreferredFilter' style={{ fontSize: '12px' }}>Preferred:</label>
              <div className={styles.dropdownContainer}>
                <Dropdown options={preferredOptions} initialSelectedValue={preferredFilter} onValueChange={handlePreferredFilter} disabled={clientListLoading} />
              </div>
            </div>
          )}

          {/* Tags removed for clients */}
        </div>

        <div className={styles.SearchBar}>
          {admin && (
            <div className={`${styles.AddBtn} ${clientListLoading ? styles.AddDisabled : ''}`} onClick={!clientListLoading ? () => setAddNewPopUp(true) : null}>
              ADD
            </div>
          )}
          <SearchBar value={inputValue} onChange={handleSearch} onKeyDown={handleKeyDown} disabled={clientListLoading} onClick={handleSearchSubmit} />

        </div>
      </div>

      {clientListLoading ? (
        <React.Fragment>
          <ClientListTable
            rows={[]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            loading={clientListLoading}
          />

          <ProjectTablePagination
            currentPage={currentPageValue}
            totalPages={totalPages}
            onPrevPage={paginationLoading ? null : handlePrevPage}
            onNextPage={paginationLoading ? null : handleNextPage}
            rowsShown={clientList.rows.length}
            totalRows={clientList.totalRows}
            rowsPerPage={rowsPerPage}
            loading={clientListLoading}
            onPageChange={handlePageChange}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <ClientListTable
            rows={clientList.rows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            loading={clientListLoading}
          />

          <ProjectTablePagination
            currentPage={currentPageValue}
            totalPages={totalPages}
            onPrevPage={paginationLoading ? null : handlePrevPage}
            onNextPage={paginationLoading ? null : handleNextPage}
            rowsShown={clientList.rows.length}
            totalRows={clientList.totalRows}
            rowsPerPage={rowsPerPage}
            loading={clientListLoading}
            onPageChange={handlePageChange}
          />

          {isResponseShown && error && error !== null && (
            <ApiResponseDisplay
              apiResponse={error}
              setIsResponseShown={setIsResponseShown}
              onClose={() => dispatch(hasError(null))}
            />
          )}

          {addNewPopUp && (
            <ClientForm
              Name={''}
              onSubmit={handleAddNewClientSubmit}
              onClose={() => setAddNewPopUp(false)}
            />
          )}
        </React.Fragment>
      )}
    </div>
  )
}

export default ClientList;